// src/pages/Inicio.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import { generarPDF } from "../utils/generarPDF";
import { generarRemisionPDF as generarRemision } from "../utils/generarRemision";
import Protegido from "../components/Protegido";
import { saldoPedido, estaPagado as pedidoPagado } from "../utils/saldoPedido";
import { fechaLocal, fechaHoy } from "../utils/internacional";
import "../estilos/EstilosGlobales.css";

// ========== COMPONENTES INTERNOS ==========

// Icono de estado de pago
const IconoPago = ({ orden }) => {
  const saldo = saldoPedido(orden);
  const estaPagado = pedidoPagado(orden);

  return (
    <div
      className={`sw-icono-pago ${estaPagado ? 'pagado' : 'pendiente'}`}
      title={estaPagado ? "Pagado" : `Saldo: $${saldo.toLocaleString()}`}
    >
      $
    </div>
  );
};

// Helper para formatear fechas.
// ⚠️ Una fecha de calendario que llega como texto "AAAA-MM-DD" NO se convierte:
// new Date() la lee como medianoche UTC y en Colombia devuelve el día anterior.
// fechaLocal ya devuelve el texto tal cual cuando la fecha viene en ese formato.
const soloFecha = (valor) => fechaLocal(valor);

// Suma días a una fecha de calendario y devuelve texto "AAAA-MM-DD".
// Se ancla al mediodía para que ningún ajuste horario mueva el día.
const sumarDias = (valor, dias) => {
  const base = fechaLocal(valor);
  if (!base) return "";
  const d = new Date(`${base}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return fechaLocal(d);
};

// Formato corto dd/mm para tarjetas
const fechaCorta = (valor) => {
  if (!valor) return null;
  const s = String(valor).trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : null;
};

// Formato moneda
const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

// ========== COMPONENTE PRINCIPAL ==========
const Inicio = () => {
  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem("usuario"));

  // Estados para datos
  const [ordenesProximas, setOrdenesProximas] = useState([]);
  const [cotizacionesProximas, setCotizacionesProximas] = useState([]);
  const [ordenesPendientes, setOrdenesPendientes] = useState([]);
  const [ordenesSaldoPendiente, setOrdenesSaldoPendiente] = useState([]);
  const [devolucionesVencidas, setDevolucionesVencidas] = useState([]);
  const [citasProximas, setCitasProximas] = useState([]);
  const [notasHoy, setNotasHoy] = useState([]);
  const [pagosProvPendientes, setPagosProvPendientes] = useState([]);

  // Estados para pestañas
  const [tabIzquierda, setTabIzquierda] = useState("pedidos");
  const [tabDerecha, setTabDerecha] = useState("revisar"); // "revisar" | "cobrar" | "pagar"

  // Consulta rápida de stock
  const [busqProd, setBusqProd] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [prodSel, setProdSel] = useState(null);
  const [fechaConsulta, setFechaConsulta] = useState("");
  const [stockConsulta, setStockConsulta] = useState(null);
  const [cargandoStock, setCargandoStock] = useState(false);

  // Nombre del usuario logueado
  const [nombreUsuario, setNombreUsuario] = useState("");
  const seleccionandoRef = useRef(false);

  useEffect(() => {
    const cargarNombreUsuario = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("nombre")
        .eq("id", user.id)
        .single();
      if (profile?.nombre) {
        const primerNombre = profile.nombre.trim().split(/\s+/)[0];
        setNombreUsuario(primerNombre.charAt(0).toUpperCase() + primerNombre.slice(1).toLowerCase());
      }
    };
    cargarNombreUsuario();
  }, []);

  // ───────────────────── Cargar órdenes ─────────────────────
  useEffect(() => {
    const cargarOrdenes = async () => {
      const { data, error } = await supabase
        .from("ordenes_pedido")
        .select("*, clientes(*)")
        .order("fecha_evento", { ascending: true });

      if (error) {
        console.error("Error cargando órdenes:", error);
        return;
      }

      // Las fechas de evento son de calendario. Se comparan como texto
      // "AAAA-MM-DD": ese formato ordena igual que una fecha y no depende de la
      // zona horaria. Pasarlas por new Date() las leía como medianoche en
      // Londres, y en Colombia el evento de HOY aparecía como vencido.
      const hoyStr = fechaHoy();

      // Último día del evento, como texto. En pedidos de varios días es la
      // fecha mayor: ordenar textos "AAAA-MM-DD" alfabéticamente da el mismo
      // resultado que ordenarlos por fecha.
      const ultimoDiaEventoTexto = (o) => {
        if (o.multi_dias && Array.isArray(o.fechas_evento) && o.fechas_evento.length > 0) {
          const fechas = o.fechas_evento.map((d) => fechaLocal(d)).filter(Boolean).sort();
          if (fechas.length > 0) return fechas[fechas.length - 1];
        }
        return fechaLocal(o.fecha_evento);
      };

      // Pedidos próximos (fecha_evento >= hoy)
      const proximas = (data || []).filter((o) => {
        const f = fechaLocal(o.fecha_evento);
        return f !== "" && f >= hoyStr;
      });

      // Pedidos pendientes de revisar (vencidos y no revisados)
      const vencidas = (data || []).filter((o) => {
        if (o.revisada) return false;
        const ultimoDia = ultimoDiaEventoTexto(o);
        return ultimoDia !== "" && ultimoDia < hoyStr;
      });

      // Pedidos con saldo pendiente (clientes que deben)
      const conSaldo = (data || []).filter(
        (o) => saldoPedido(o) > 0 && Number(o.total_neto || o.total || 0) > 0
      );

      setOrdenesProximas(proximas);
      setOrdenesPendientes(vencidas);
      setOrdenesSaldoPendiente(conSaldo);

      // Devoluciones vencidas: pasó fecha_devolucion y no se marcó mercancia_devuelta
      const devVencidas = (data || []).filter((o) => {
        if (o.cerrada || o.mercancia_devuelta) return false;
        let fechaDev = fechaLocal(o.fecha_devolucion);
        if (!fechaDev) {
          const ultimoDia = ultimoDiaEventoTexto(o);
          if (!ultimoDia) return false;
          fechaDev = sumarDias(ultimoDia, 1);
        }
        return fechaDev !== "" && fechaDev < hoyStr;
      });
      setDevolucionesVencidas(devVencidas);

      // Pagos pendientes a proveedores
      const provPend = [];
      (data || []).forEach((o) => {
        const pagos = o.pagos_proveedores;
        if (!Array.isArray(pagos) || pagos.length === 0) return;
        pagos.forEach((pp) => {
          const totalCompra = Number(pp.total || pp.subtotal_compra || 0);
          if (totalCompra <= 0) return;
          const abonado = (pp.abonos || []).reduce((s, a) => s + Number(a.valor || 0), 0);
          const saldo = totalCompra - abonado;
          if (saldo > 0) {
            provPend.push({
              orden: o, // referencia completa para navegación
              orden_numero: o.numero || "OP-???",
              fecha_evento: o.fecha_evento,
              proveedor_nombre: pp.proveedor_nombre || pp.nombre || "Proveedor",
              producto_nombre: pp.nombre || "Producto",
              total_compra: totalCompra,
              abonado,
              saldo,
            });
          }
        });
      });
      setPagosProvPendientes(provPend);
    };

    cargarOrdenes();
  }, []);

  // ───────────────────── Cargar cotizaciones próximas ─────────────────────
  useEffect(() => {
    const cargarCotizaciones = async () => {
      const { data, error } = await supabase
        .from("cotizaciones")
        .select("*, clientes(*)")
        .order("fecha_evento", { ascending: true });

      if (error) {
        console.error("Error cargando cotizaciones:", error);
        return;
      }

      // Las fechas de evento son de calendario: se comparan como texto.
      const hoyStr = fechaHoy();

      const proximas = (data || []).filter((c) => {
        const f = fechaLocal(c.fecha_evento);
        return f !== "" && f >= hoyStr;
      });
      
      setCotizacionesProximas(proximas);
    };

    cargarCotizaciones();
  }, []);

  // ───────────────────── Cargar citas próximas (hoy + mañana) ─────────────────────
  useEffect(() => {
    const cargarCitas = async () => {
      const hoy = new Date();
      const manana = new Date(hoy);
      manana.setDate(manana.getDate() + 1);
      const fHoy = hoy.toLocaleDateString("en-CA");
      const fManana = manana.toLocaleDateString("en-CA");

      const { data, error } = await supabase
        .from("citas")
        .select("id, fecha, hora, motivo, estado, notas, clientes(nombre, telefono)")
        .in("fecha", [fHoy, fManana])
        .in("estado", ["programada", "confirmada"])
        .order("fecha")
        .order("hora");

      if (!error && data) setCitasProximas(data);
    };
    cargarCitas();
  }, []);

  // ───────────────────── Cargar notas de hoy ─────────────────────
  useEffect(() => {
    const cargarNotas = async () => {
      const hoy = new Date().toLocaleDateString("en-CA");
      const { data, error } = await supabase
        .from("agenda")
        .select("id, titulo, descripcion, fecha")
        .eq("fecha", hoy)
        .order("created_at", { ascending: true });

      if (!error && data) setNotasHoy(data);
    };
    cargarNotas();
  }, []);

  // ───────────────────── Sugerencias por nombre (mín. 2 letras) ────────────────────
  useEffect(() => {
    if (seleccionandoRef.current) {
      seleccionandoRef.current = false;
      return;
    }
    const fetch = async () => {
      if (!busqProd || busqProd.trim().length < 2) {
        setSugerencias([]);
        return;
      }
      const { data, error } = await supabase
        .from("productos")
        .select("id, nombre, stock")
        .ilike("nombre", `%${busqProd}%`)
        .limit(10);

      if (error) {
        console.error("Error buscando productos:", error);
        setSugerencias([]);
        return;
      }
      setSugerencias(data || []);
    };
    fetch();
  }, [busqProd]);

  // ───────────────────── Calcular stock por fecha ─────────────────────
  const calcularStockParaFecha = async (productoId, fechaISO) => {
    setCargandoStock(true);
    try {
      const { data: prod } = await supabase
        .from("productos")
        .select("id, stock")
        .eq("id", productoId)
        .single();
      const stockBase = parseInt(prod?.stock ?? 0, 10);

      const { data: ords } = await supabase
        .from("ordenes_pedido")
        .select("productos, fecha_evento, fechas_evento, fecha_entrega, fecha_devolucion, cerrada")
        .eq("cerrada", false);

      const ordenes = ords || [];
      const fecha = String(fechaISO).slice(0, 10);

      let reservado = 0;

      ordenes.forEach((o) => {
        const diasEvento = [
          ...(o.fecha_evento ? [String(o.fecha_evento).slice(0, 10)] : []),
          ...((o.fechas_evento || []).map((d) => String(d).slice(0, 10))),
        ].sort();
        const ultimoDiaEvento = diasEvento[diasEvento.length - 1];
        if (!ultimoDiaEvento) return;

        const entrega = o.fecha_entrega
          ? String(o.fecha_entrega).slice(0, 10)
          : String(o.fecha_evento).slice(0, 10);

        let devolucion;
        if (o.fecha_devolucion) {
          devolucion = String(o.fecha_devolucion).slice(0, 10);
        } else {
          const d = new Date(ultimoDiaEvento + "T12:00:00");
          d.setDate(d.getDate() + 1);
          devolucion = d.toISOString().slice(0, 10);
        }

        if (fecha < entrega || fecha > devolucion) return;

        (o.productos || []).forEach((p) => {
          if (p.es_grupo && Array.isArray(p.productos)) {
            p.productos.forEach((sub) => {
              const id = sub.producto_id || sub.id;
              const cant = (Number(sub.cantidad) || 0) * (Number(p.cantidad) || 1);
              if (id === productoId) {
                reservado += cant;
              }
            });
          } else {
            const id = p.producto_id || p.id;
            if (id === productoId) {
              reservado += (Number(p.cantidad) || 0);
            }
          }
        });
      });

      setStockConsulta(stockBase - reservado);
    } catch (e) {
      console.error("Error calculando stock:", e);
      setStockConsulta(null);
    } finally {
      setCargandoStock(false);
    }
  };

  // Recalcular cuando haya producto y fecha
  useEffect(() => {
    if (prodSel?.id && fechaConsulta) {
      calcularStockParaFecha(prodSel.id, fechaConsulta);
    } else {
      setStockConsulta(null);
    }
  }, [prodSel, fechaConsulta]);

  // ───────────────────── Acciones UI ─────────────────────
  const editarOrden = (orden) => {
    const cliente = orden.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          ...orden,
          nombre_cliente: cliente.nombre || "",
          identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "",
          direccion: cliente.direccion || "",
          email: cliente.email || "",
          fecha_creacion: orden.fecha_creacion || orden.fecha || null,
          abonos: orden.abonos || [],
          garantia: orden.garantia || "",
          fechaGarantia: orden.fechaGarantia || "",
          garantiaRecibida: orden.garantiaRecibida || false,
          estado: orden.estado || "",
          numero: orden.numero || "",
          esEdicion: true,
          idOriginal: orden.id,
        },
        tipo: "orden",
      },
    });
  };

  const editarCotizacion = (cotizacion) => {
    const cliente = cotizacion.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          ...cotizacion,
          nombre_cliente: cliente.nombre || "",
          identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "",
          direccion: cliente.direccion || "",
          email: cliente.email || "",
          fecha_creacion: cotizacion.fecha_creacion || cotizacion.fecha || null,
          estado: cotizacion.estado || "",
          numero: cotizacion.numero || "",
          esEdicion: true,
          idOriginal: cotizacion.id,
        },
        tipo: "cotizacion",
      },
    });
  };

  const manejarPDF = async (orden) => {
    const doc = {
      ...orden,
      nombre_cliente: orden.clientes?.nombre || "N/A",
      identificacion: orden.clientes?.identificacion || "N/A",
      telefono: orden.clientes?.telefono || "N/A",
      direccion: orden.clientes?.direccion || "N/A",
      email: orden.clientes?.email || "N/A",
      fecha_creacion: soloFecha(orden.fecha_creacion || orden.fecha),
      fecha_evento: soloFecha(orden.fecha_evento),
    };
    await generarPDF(doc, "orden");
  };

  const manejarPDFCotizacion = async (cotizacion) => {
    const doc = {
      ...cotizacion,
      nombre_cliente: cotizacion.clientes?.nombre || "N/A",
      identificacion: cotizacion.clientes?.identificacion || "N/A",
      telefono: cotizacion.clientes?.telefono || "N/A",
      direccion: cotizacion.clientes?.direccion || "N/A",
      email: cotizacion.clientes?.email || "N/A",
      fecha_creacion: soloFecha(cotizacion.fecha_creacion || cotizacion.fecha),
      fecha_evento: soloFecha(cotizacion.fecha_evento),
    };
    await generarPDF(doc, "cotizacion");
  };

  const manejarRemision = async (orden) => {
    const doc = {
      ...orden,
      nombre_cliente: orden.clientes?.nombre || "N/A",
      identificacion: orden.clientes?.identificacion || "N/A",
      telefono: orden.clientes?.telefono || "N/A",
      direccion: orden.clientes?.direccion || "N/A",
      email: orden.clientes?.email || "N/A",
      fecha_creacion: soloFecha(orden.fecha_creacion || orden.fecha),
      fecha_evento: soloFecha(orden.fecha_evento),
    };
    await generarRemision(doc);
  };

  const calcularSaldo = (orden) => saldoPedido(orden);

  // Las dos listas de "Revisar" se pisan casi por completo: un pedido sin
  // revisar casi siempre tiene también la devolución vencida. Se juntan en
  // una sola, sin repetidos, para que el contador no muestre el doble.
  const idsPorRevisar = new Set(ordenesPendientes.map((o) => o.id));
  const pendientesRevisar = [
    ...ordenesPendientes,
    ...devolucionesVencidas.filter((o) => !idsPorRevisar.has(o.id)),
  ];

  // Total pendiente a proveedores
  const totalDeudaProv = pagosProvPendientes.reduce((s, p) => s + p.saldo, 0);

  // ═══════════════════════════════════════════════════════════════
  // ESTILOS
  // ═══════════════════════════════════════════════════════════════
  const estilos = {
    pagina: {
      minHeight: "calc(100vh - 56px)",
      backgroundColor: "#ffffff",
      padding: "0",
    },
    contenido: {
      maxWidth: "1000px",
      margin: "0 auto",
      padding: "20px",
      paddingLeft: "0px",
    },
    header: {
      marginBottom: "20px",
    },
    titulo: {
      fontSize: "24px",
      fontWeight: "600",
      color: "#1f2937",
      borderLeft: "4px solid #00B4D8",
      paddingLeft: "12px",
    },
    gridPedidos: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: "24px",
      marginBottom: "24px",
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      minHeight: "340px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      transition: "border-color 0.3s ease",
    },
    cardCompacta: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    tabsContainer: {
      display: "flex",
      gap: "0",
    },
    tab: {
      flex: 1,
      padding: "12px 6px",
      border: "none",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
      transition: "all 0.3s ease",
      borderRadius: "0",
    },
    tabActivePedidos: {
      background: "linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)",
      color: "#ffffff",
    },
    tabInactivePedidos: {
      background: "#e0f2fe",
      color: "#0077B6",
    },
    tabActiveCotizaciones: {
      background: "linear-gradient(135deg, #86A789 0%, #739072 100%)",
      color: "#ffffff",
    },
    tabInactiveCotizaciones: {
      background: "#dcfce7",
      color: "#166534",
    },
    tabActiveRevisar: {
      background: "linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)",
      color: "#dc2626",
    },
    tabInactiveRevisar: {
      background: "#fee2e2",
      color: "#dc2626",
    },
    tabActiveCobrar: {
      background: "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
      color: "#ffffff",
    },
    tabInactiveCobrar: {
      background: "#ede9fe",
      color: "#6d28d9",
    },
    tabActivePagar: {
      background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
      color: "#78350f",
    },
    tabInactivePagar: {
      background: "#fef3c7",
      color: "#92400e",
    },
    cardBody: {
      padding: "16px",
      maxHeight: "280px",
      overflowY: "auto",
    },
    cardBodyCompacta: {
      padding: "12px",
      maxHeight: "180px",
      overflowY: "auto",
    },
    cardHeader: {
      padding: "10px 16px",
      fontSize: 13,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 8,
      color: "#fff",
    },
    pedidoCard: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px",
      backgroundColor: "#fafafa",
      borderRadius: "8px",
      marginBottom: "8px",
      border: "1px solid rgba(0,0,0,0.05)",
    },
    pedidoInfo: {
      flex: 1,
    },
    pedidoNumero: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#0077B6",
      margin: "0 0 2px 0",
    },
    pedidoCliente: {
      fontSize: "13px",
      color: "#374151",
      margin: "0 0 2px 0",
    },
    pedidoFecha: {
      fontSize: "12px",
      color: "#9ca3af",
      margin: 0,
    },
    pedidoSaldo: {
      fontSize: "12px",
      color: "#dc2626",
      fontWeight: "500",
      margin: 0,
    },
    pedidoAcciones: {
      display: "flex",
      gap: "6px",
    },
    btnIcono: {
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(0,180,216,0.1)",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px",
      transition: "background-color 0.2s",
    },
    noData: {
      textAlign: "center",
      color: "#9ca3af",
      padding: "24px 0",
      fontSize: "14px",
    },
    noDataCompacta: {
      textAlign: "center",
      color: "#9ca3af",
      padding: "16px 0",
      fontSize: "13px",
    },
    badge: {
      fontSize: 9, padding: "1px 6px", borderRadius: 10, fontWeight: 600,
    },
  };

  return (
    <Protegido>
      <div style={estilos.pagina}>
        <div style={estilos.contenido}>
          
          {/* Header */}
          <div style={estilos.header}>
            <h1 style={estilos.titulo}>Bienvenido{nombreUsuario ? `, ${nombreUsuario}` : ""} 👋</h1>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              FILA 1: Pedidos/Cotizaciones + Revisar/Cobrar/Pagar
          ═══════════════════════════════════════════════════════════ */}
          <div style={estilos.gridPedidos}>
            
            {/* CUADRO IZQUIERDO: Pedidos / Cotizaciones próximas */}
            <div style={{
              ...estilos.card,
              borderColor: tabIzquierda === "pedidos" ? "rgba(0,180,216,0.3)" : "rgba(134,167,137,0.3)",
            }}>
              <div style={estilos.tabsContainer}>
                <button
                  onClick={() => setTabIzquierda("pedidos")}
                  style={{
                    ...estilos.tab,
                    ...(tabIzquierda === "pedidos" ? estilos.tabActivePedidos : estilos.tabInactivePedidos),
                    borderTopLeftRadius: "12px",
                  }}
                >
                  📅 Próximos pedidos
                </button>
                <button
                  onClick={() => setTabIzquierda("cotizaciones")}
                  style={{
                    ...estilos.tab,
                    ...(tabIzquierda === "cotizaciones" ? estilos.tabActiveCotizaciones : estilos.tabInactiveCotizaciones),
                    borderTopRightRadius: "12px",
                  }}
                >
                  📅 Próximas cotizaciones
                </button>
              </div>

              <div style={estilos.cardBody}>
                {tabIzquierda === "pedidos" ? (
                  ordenesProximas.length === 0 ? (
                    <p style={estilos.noData}>No hay pedidos próximos</p>
                  ) : (
                    ordenesProximas.slice(0, 10).map((orden) => (
                      <div key={orden.id} style={estilos.pedidoCard}>
                        <div style={estilos.pedidoInfo}>
                          <p style={estilos.pedidoNumero}>{orden.numero || "OP-???"}</p>
                          <p style={estilos.pedidoCliente}>{orden.clientes?.nombre || "Cliente"}</p>
                          <p style={estilos.pedidoFecha}>{soloFecha(orden.fecha_evento) || "-"}</p>
                          {(orden.fecha_entrega || orden.fecha_devolucion) && (
                            <p style={{ fontSize: 10, color: "#9ca3af", margin: "2px 0 0" }}>
                              {fechaCorta(orden.fecha_entrega) && `📦 ${fechaCorta(orden.fecha_entrega)}`}
                              {orden.fecha_entrega && orden.fecha_devolucion && "  ·  "}
                              {fechaCorta(orden.fecha_devolucion) && `📥 ${fechaCorta(orden.fecha_devolucion)}`}
                            </p>
                          )}
                        </div>
                        <div style={estilos.pedidoAcciones}>
                          <IconoPago orden={orden} />
                          <button style={estilos.btnIcono} onClick={() => editarOrden(orden)} title="Editar">✏️</button>
                          <button style={estilos.btnIcono} onClick={() => manejarPDF(orden)} title="PDF">📄</button>
                          <button style={estilos.btnIcono} onClick={() => manejarRemision(orden)} title="Remisión">🚚</button>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  cotizacionesProximas.length === 0 ? (
                    <p style={estilos.noData}>No hay cotizaciones próximas</p>
                  ) : (
                    cotizacionesProximas.slice(0, 10).map((cot) => (
                      <div key={cot.id} style={{
                        ...estilos.pedidoCard,
                        backgroundColor: "#f0fdf4",
                        border: "1px solid rgba(134,167,137,0.2)",
                      }}>
                        <div style={estilos.pedidoInfo}>
                          <p style={{ ...estilos.pedidoNumero, color: "#166534" }}>{cot.numero || "COT-???"}</p>
                          <p style={estilos.pedidoCliente}>{cot.clientes?.nombre || "Cliente"}</p>
                          <p style={estilos.pedidoFecha}>{soloFecha(cot.fecha_evento) || "-"}</p>
                        </div>
                        <div style={estilos.pedidoAcciones}>
                          <button style={{ ...estilos.btnIcono, backgroundColor: "rgba(134,167,137,0.15)" }} onClick={() => editarCotizacion(cot)} title="Editar">✏️</button>
                          <button style={{ ...estilos.btnIcono, backgroundColor: "rgba(134,167,137,0.15)" }} onClick={() => manejarPDFCotizacion(cot)} title="PDF">📄</button>
                        </div>
                      </div>
                    ))
                  )
                )}

                {tabIzquierda === "pedidos" && ordenesProximas.length > 0 && (
                  <div style={{ textAlign: "center", padding: "8px 0 4px", borderTop: "1px solid #f3f4f6" }}>
                    <button onClick={() => navigate("/ruta-entregas")}
                      style={{ background: "none", border: "none", color: "#0077B6", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                      Ver todos ({ordenesProximas.length}) →
                    </button>
                  </div>
                )}

                {tabIzquierda === "cotizaciones" && cotizacionesProximas.length > 0 && (
                  <div style={{ textAlign: "center", padding: "8px 0 4px", borderTop: "1px solid #f3f4f6" }}>
                    <button onClick={() => navigate("/cotizaciones-guardadas")}
                      style={{ background: "none", border: "none", color: "#166534", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                      Ver todas las cotizaciones ({cotizacionesProximas.length}) →
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* CUADRO DERECHO: Revisar / Cobrar / Pagar */}
            <div style={{
              ...estilos.card,
              borderColor: tabDerecha === "revisar" ? "rgba(239, 68, 68, 0.3)"
                : tabDerecha === "cobrar" ? "rgba(139, 92, 246, 0.3)"
                : "rgba(245, 158, 11, 0.3)",
            }}>
              {/* 3 Pestañas */}
              <div style={estilos.tabsContainer}>
                <button
                  onClick={() => setTabDerecha("revisar")}
                  style={{
                    ...estilos.tab,
                    ...(tabDerecha === "revisar" ? estilos.tabActiveRevisar : estilos.tabInactiveRevisar),
                    borderTopLeftRadius: "12px",
                  }}
                >
                  ⚠️ Revisar {pendientesRevisar.length > 0 && (
                    <span style={{ ...estilos.badge, background: "rgba(220,38,38,0.15)", color: "#dc2626" }}>
                      {pendientesRevisar.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setTabDerecha("cobrar")}
                  style={{
                    ...estilos.tab,
                    ...(tabDerecha === "cobrar" ? estilos.tabActiveCobrar : estilos.tabInactiveCobrar),
                  }}
                >
                  💰 Cobrar {ordenesSaldoPendiente.length > 0 && (
                    <span style={{ ...estilos.badge, background: "rgba(109,40,217,0.15)", color: "#6d28d9" }}>
                      {ordenesSaldoPendiente.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setTabDerecha("pagar")}
                  style={{
                    ...estilos.tab,
                    ...(tabDerecha === "pagar" ? estilos.tabActivePagar : estilos.tabInactivePagar),
                    borderTopRightRadius: "12px",
                  }}
                >
                  🏢 Pagar {pagosProvPendientes.length > 0 && (
                    <span style={{ ...estilos.badge, background: "rgba(146,64,14,0.15)", color: "#92400e" }}>
                      {pagosProvPendientes.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Contenido según pestaña */}
              <div style={estilos.cardBody}>
                {tabDerecha === "revisar" ? (
                  <>
                    {/* Pedidos por revisar */}
                    {pendientesRevisar.length === 0 ? (
                      <p style={estilos.noData}>Todo al día 🎉</p>
                    ) : (
                      <>
                        {pendientesRevisar.slice(0, 10).map((orden) => {
                          // Días de retraso: sobre la fecha de devolución, o el
                          // día siguiente al evento si el pedido no la tiene.
                          let fechaDev = fechaLocal(orden.fecha_devolucion);
                          if (!fechaDev) {
                            let ultimoDia = fechaLocal(orden.fecha_evento);
                            if (orden.multi_dias && Array.isArray(orden.fechas_evento) && orden.fechas_evento.length > 0) {
                              const sorted = orden.fechas_evento.map((d) => fechaLocal(d)).filter(Boolean).sort();
                              if (sorted.length > 0) ultimoDia = sorted[sorted.length - 1];
                            }
                            if (ultimoDia) fechaDev = sumarDias(ultimoDia, 1);
                          }
                          const diasRetraso = fechaDev
                            ? Math.floor((new Date(`${fechaHoy()}T12:00:00`) - new Date(`${fechaDev}T12:00:00`)) / 86400000)
                            : 0;
                            return (
                            <div key={orden.id} style={{
                              ...estilos.pedidoCard,
                              backgroundColor: "#fef2f2",
                              border: "1px solid rgba(239, 68, 68, 0.15)",
                            }}>
                              <div style={estilos.pedidoInfo}>
                                <p style={{ ...estilos.pedidoNumero, color: '#dc2626' }}>{orden.numero || "OP-???"}</p>
                                <p style={estilos.pedidoCliente}>{orden.clientes?.nombre || "Cliente"}</p>
                                <p style={estilos.pedidoFecha}>{soloFecha(orden.fecha_evento) || "-"}</p>
                                {diasRetraso > 0 && (
                                  <p style={{ fontSize: 11, color: "#dc2626", fontWeight: 600, margin: 0 }}>
                                    ⏰ {diasRetraso} día{diasRetraso !== 1 ? "s" : ""} de retraso
                                  </p>
                                )}
                              </div>
                              <div style={estilos.pedidoAcciones}>
                                <IconoPago orden={orden} />
                                <button style={{ ...estilos.btnIcono, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                                  onClick={() => navigate(`/recepcion?id=${orden.id}`)} title="Revisar">📝</button>
                                <button style={{ ...estilos.btnIcono, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                                  onClick={() => editarOrden(orden)} title="Editar">✏️</button>
                              </div>
                            </div>
                          );
                        })}
                        <div style={{ textAlign: "center", padding: "8px 0 4px", borderTop: "1px solid #f3f4f6" }}>
                          <button onClick={() => navigate("/recepcion")}
                            style={{ background: "none", border: "none", color: "#dc2626", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                            Ver todos ({pendientesRevisar.length}) →
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : tabDerecha === "cobrar" ? (
                  <>
                    {ordenesSaldoPendiente.length === 0 ? (
                      <p style={estilos.noData}>No hay pagos pendientes</p>
                    ) : (
                      ordenesSaldoPendiente.slice(0, 10).map((orden) => (
                        <div key={orden.id} style={{
                          ...estilos.pedidoCard,
                          backgroundColor: "#faf5ff",
                          border: "1px solid rgba(139, 92, 246, 0.15)",
                        }}>
                          <div style={estilos.pedidoInfo}>
                            <p style={{ ...estilos.pedidoNumero, color: '#6d28d9' }}>{orden.numero || "OP-???"}</p>
                            <p style={estilos.pedidoCliente}>{orden.clientes?.nombre || "Cliente"}</p>
                            <p style={estilos.pedidoFecha}>{soloFecha(orden.fecha_evento) || "-"}</p>
                            <p style={estilos.pedidoSaldo}>Saldo: {money(calcularSaldo(orden))}</p>
                          </div>
                          <div style={estilos.pedidoAcciones}>
                            <button style={{ ...estilos.btnIcono, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                              onClick={() => editarOrden(orden)} title="Ver pedido">✏️</button>
                            <button style={{ ...estilos.btnIcono, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                              onClick={() => manejarPDF(orden)} title="PDF">📄</button>
                          </div>
                        </div>
                      ))
                    )}
                    {ordenesSaldoPendiente.length > 0 && (
                      <div style={{ textAlign: "center", padding: "8px 0 4px", borderTop: "1px solid #f3f4f6" }}>
                        <button onClick={() => navigate("/pagos-pendientes")}
                          style={{ background: "none", border: "none", color: "#6d28d9", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                          Ver todos ({ordenesSaldoPendiente.length}) →
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  /* PAGAR — Pagos pendientes a proveedores */
                  <>
                    {pagosProvPendientes.length === 0 ? (
                      <p style={estilos.noData}>No hay pagos pendientes a proveedores</p>
                    ) : (
                      <>
                        {/* Resumen total */}
                        <div style={{
                          background: "linear-gradient(135deg, #fffbeb, #fef3c7)",
                          border: "1px solid #fde68a",
                          borderRadius: 8,
                          padding: "10px 12px",
                          marginBottom: 10,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}>
                          <span style={{ fontSize: 12, color: "#92400e", fontWeight: 500 }}>
                            Total por pagar:
                          </span>
                          <span style={{ fontSize: 15, color: "#78350f", fontWeight: 700 }}>
                            {money(totalDeudaProv)}
                          </span>
                        </div>

                        {pagosProvPendientes.slice(0, 10).map((pp, idx) => (
                          <div key={`prov-${idx}`} style={{
                            ...estilos.pedidoCard,
                            backgroundColor: "#fffbeb",
                            border: "1px solid rgba(245,158,11,0.2)",
                          }}>
                            <div style={estilos.pedidoInfo}>
                              <p style={{ ...estilos.pedidoNumero, color: '#92400e' }}>{pp.orden_numero}</p>
                              <p style={estilos.pedidoCliente}>🏢 {pp.proveedor_nombre}</p>
                              <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>{pp.producto_nombre}</p>
                              <p style={{ fontSize: 12, color: "#b45309", fontWeight: 500, margin: 0 }}>
                                Debes: {money(pp.saldo)}
                                {pp.abonado > 0 && <span style={{ color: "#9ca3af", fontWeight: 400 }}> (abonado: {money(pp.abonado)})</span>}
                              </p>
                            </div>
                            <div style={estilos.pedidoAcciones}>
                              <button style={{ ...estilos.btnIcono, backgroundColor: 'rgba(245,158,11,0.1)' }}
                                onClick={() => editarOrden(pp.orden)} title="Ver pedido">✏️</button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    {pagosProvPendientes.length > 0 && (
                      <div style={{ textAlign: "center", padding: "8px 0 4px", borderTop: "1px solid #f3f4f6" }}>
                        <button onClick={() => navigate("/cuentas-por-pagar")}
                          style={{ background: "none", border: "none", color: "#d97706", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                          Ver todas las deudas ({pagosProvPendientes.length}) →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              FILA 2: Citas próximas + Notas del día
          ═══════════════════════════════════════════════════════════ */}
          <div style={estilos.gridPedidos}>

            {/* CITAS PRÓXIMAS */}
            <div style={{
              ...estilos.cardCompacta,
              borderColor: "rgba(168, 85, 247, 0.25)",
            }}>
              <div style={{
                ...estilos.cardHeader,
                background: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
              }}>
                📅 Citas próximas
                {citasProximas.length > 0 && (
                  <span style={{
                    background: "rgba(255,255,255,0.25)",
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontSize: 11,
                  }}>{citasProximas.length}</span>
                )}
              </div>
              <div style={estilos.cardBodyCompacta}>
                {citasProximas.length === 0 ? (
                  <p style={estilos.noDataCompacta}>No hay citas para hoy ni mañana</p>
                ) : (
                  citasProximas.slice(0, 4).map((cita) => {
                    const hoyStr = new Date().toLocaleDateString("en-CA");
                    const esHoy = cita.fecha === hoyStr;
                    const clienteNombre = cita.clientes?.nombre || "Cliente";
                    const clienteTel = cita.clientes?.telefono || "";
                    const horaCorta = (cita.hora || "").slice(0, 5);

                    return (
                      <div key={cita.id} style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 10px",
                        backgroundColor: esHoy ? "#faf5ff" : "#f8fafc",
                        borderRadius: 8,
                        marginBottom: 6,
                        border: esHoy ? "1px solid rgba(168,85,247,0.2)" : "1px solid rgba(0,0,0,0.05)",
                        borderLeft: esHoy ? "3px solid #a78bfa" : "3px solid #cbd5e1",
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 1 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#7c3aed" }}>{horaCorta || "—"}</span>
                            <span style={{ ...estilos.badge, background: esHoy ? "#ede9fe" : "#e0f2fe", color: esHoy ? "#7c3aed" : "#0369a1" }}>
                              {esHoy ? "HOY" : "MAÑANA"}
                            </span>
                            <span style={{ ...estilos.badge, background: cita.estado === "confirmada" ? "#dcfce7" : "#fef9c3", color: cita.estado === "confirmada" ? "#15803d" : "#a16207" }}>
                              {cita.estado === "confirmada" ? "✓" : "●"}
                            </span>
                          </div>
                          <p style={{ fontSize: 12, color: "#374151", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{clienteNombre}</p>
                          {cita.motivo && <p style={{ fontSize: 10, color: "#9ca3af", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cita.motivo}</p>}
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          {clienteTel && (
                            <a
                              href={`https://wa.me/57${clienteTel.replace(/\D/g, "")}?text=${encodeURIComponent(
                                `Hola ${clienteNombre}, le recordamos su cita ${esHoy ? "hoy" : "mañana"} a las ${horaCorta}${cita.motivo ? ` — ${cita.motivo}` : ""}. ¡Lo esperamos!`
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                                backgroundColor: "rgba(37,211,102,0.1)", border: "none", borderRadius: 6,
                                fontSize: 13, textDecoration: "none",
                              }}
                              title="WhatsApp"
                            >💬</a>
                          )}
                          <button
                            style={{ ...estilos.btnIcono, width: 28, height: 28, fontSize: 13, backgroundColor: "rgba(168,85,247,0.1)" }}
                            onClick={() => navigate("/agenda")}
                            title="Agenda"
                          >📅</button>
                        </div>
                      </div>
                    );
                  })
                )}
                {citasProximas.length > 0 && (
                  <div style={{ textAlign: "center", padding: "6px 0 2px", borderTop: "1px solid #f3f4f6" }}>
                    <button onClick={() => navigate("/agenda")}
                      style={{ background: "none", border: "none", color: "#7c3aed", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                      Ver agenda completa →
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* NOTAS DEL DÍA */}
            <div style={{
              ...estilos.cardCompacta,
              borderColor: "rgba(59, 130, 246, 0.25)",
            }}>
              <div style={{
                ...estilos.cardHeader,
                background: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
              }}>
                📝 Notas de hoy
                {notasHoy.length > 0 && (
                  <span style={{
                    background: "rgba(255,255,255,0.25)",
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontSize: 11,
                  }}>{notasHoy.length}</span>
                )}
              </div>
              <div style={estilos.cardBodyCompacta}>
                {notasHoy.length === 0 ? (
                  <p style={estilos.noDataCompacta}>No hay notas para hoy</p>
                ) : (
                  notasHoy.slice(0, 4).map((nota) => (
                    <div key={nota.id} style={{
                      padding: "8px 10px",
                      backgroundColor: "#eff6ff",
                      borderRadius: 8,
                      marginBottom: 6,
                      border: "1px solid rgba(59,130,246,0.15)",
                      borderLeft: "3px solid #60a5fa",
                    }}>
                      <p style={{ fontSize: 12, color: "#1e40af", margin: 0, fontWeight: 500 }}>
                        {nota.descripcion || nota.titulo || "—"}
                      </p>
                    </div>
                  ))
                )}
                <div style={{ textAlign: "center", padding: "6px 0 2px", borderTop: notasHoy.length > 0 ? "1px solid #f3f4f6" : "none" }}>
                  <button onClick={() => navigate("/agenda")}
                    style={{ background: "none", border: "none", color: "#3b82f6", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                    {notasHoy.length > 0 ? "Ver todas en agenda →" : "Crear nota en agenda →"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              CONSULTA RÁPIDA DE STOCK — Modernizada
          ═══════════════════════════════════════════════════════════ */}
          <div style={{
            ...estilos.cardCompacta,
            marginBottom: 24,
            borderColor: "rgba(0,180,216,0.2)",
            overflow: "visible",
          }}>
            <div style={{
              ...estilos.cardHeader,
              background: "linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)",
              padding: "10px 16px",
            }}>
              📊 Consulta rápida de stock
            </div>
            <div style={{ padding: "16px" }}>
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "center",
              }}>
                <input
                  type="date"
                  value={fechaConsulta}
                  onChange={(e) => setFechaConsulta(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    maxWidth: '160px',
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  title="Fecha a consultar"
                />
                
                <div style={{ position: 'relative', flex: 1, minWidth: '220px', zIndex: 50 }}>
                  <input
                    type="text"
                    value={busqProd}
                    onChange={(e) => {
                      setBusqProd(e.target.value);
                      setProdSel(null);
                      setStockConsulta(null);
                    }}
                    placeholder="Buscar producto del inventario..."
                    style={{
                      padding: "10px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      width: '100%',
                      boxSizing: 'border-box',
                      outline: "none",
                    }}
                  />
                  {sugerencias.length > 0 && (
                    <ul style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginTop: '4px',
                      maxHeight: '35vh',
                      overflowY: 'scroll',
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                      touchAction: 'pan-y',
                      zIndex: 200,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      listStyle: 'none',
                      padding: '0 0 70px 0',
                      margin: 0
                    }}>
                      {sugerencias.map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => {
                              seleccionandoRef.current = true;
                              setProdSel(s);
                              setBusqProd(s.nombre);
                              setSugerencias([]);
                            }}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '13px',
                              borderBottom: '1px solid #f3f4f6',
                              color: '#1f2937'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#f0f9ff'}
                            onMouseOut={(e) => e.target.style.background = 'none'}
                          >
                            {s.nombre} <span style={{ color: '#9ca3af', fontSize: 12 }}>· Stock: {s.stock}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div style={{
                  padding: "10px 20px",
                  background: stockConsulta !== null
                    ? (stockConsulta > 0 ? "linear-gradient(135deg, #ecfdf5, #d1fae5)" : "linear-gradient(135deg, #fef2f2, #fecaca)")
                    : "#f0f9ff",
                  borderRadius: "8px",
                  border: stockConsulta !== null
                    ? (stockConsulta > 0 ? "1px solid #86efac" : "1px solid #fca5a5")
                    : "1px solid #bae6fd",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: stockConsulta !== null
                    ? (stockConsulta > 0 ? "#15803d" : "#dc2626")
                    : "#0369a1",
                  minWidth: 120,
                  textAlign: "center",
                }}>
                  {prodSel && fechaConsulta
                    ? (cargandoStock ? "⏳ Calculando..." : `Disponible: ${stockConsulta ?? "—"}`)
                    : "Disponible: —"}
                </div>
              </div>
            </div>
          </div>

          {/* ─── Footer informativo ─── */}
          <div style={{
            marginTop: 32,
            padding: '20px 16px',
            background: '#f8fafc',
            borderRadius: 12,
            border: '1px solid #e5e7eb',
            textAlign: 'center',
            marginBottom: 5
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <a href="https://wa.me/573214909600?text=Hola%2C%20necesito%20ayuda%20con%20SwAlquiler" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  background: '#ecfdf5', color: '#059669',
                  fontSize: 12, fontWeight: 600, textDecoration: 'none',
                  border: '1px solid #bbf7d0'
                }}>
                💬 Soporte
              </a>
              <a href="https://www.instagram.com/swalquiler" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  background: '#fdf2f8', color: '#db2777',
                  fontSize: 12, fontWeight: 600, textDecoration: 'none',
                  border: '1px solid #fbcfe8'
                }}>
                📸 Tutoriales
              </a>
              <a href="https://www.swalquiler.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  background: '#f0f9ff', color: '#0077B6',
                  fontSize: 12, fontWeight: 600, textDecoration: 'none',
                  border: '1px solid #bae6fd'
                }}>
                🌐 swalquiler.com
              </a>
            </div>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: 0, lineHeight: 1.6 }}>
              SwAlquiler v1.0 · Gestión de alquiler y eventos
              <br />
              📧 soporte@swalquiler.com
            </p>
          </div>

        </div>
      </div>
    </Protegido>
  );
};

export default Inicio;